import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { feedApi } from "../api/feed";
import { FeedCard } from "../components/FeedCard";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import DeepDiveCard from "../components/DeepDiveCard";

/* ──────────────────────────────────────────────────────────────────
 * Intelligence — full Haiven feed.
 *
 * Reached from the Dashboard's "View all [N] signals →" gold link.
 * Full-screen push with a back arrow; intentionally NOT a bottom-
 * nav tab so the feed stays a destination (deliberate visit) rather
 * than an attention-pulling default.
 *
 * Layout:
 *   • Summary card — "This week Haiven noticed [N] things…" plus
 *     domain chips (most-recent signal per BIO/PSYCH/BEH). Chips
 *     scroll-jump to that card in the feed.
 *   • Time-bucketed feed:
 *       TODAY · YESTERDAY · THIS WEEK · EARLIER
 *     Bucket headers are gold micro-caps. EARLIER collapses by
 *     default with a "Show N more" affordance so the surface stays
 *     scannable for long-tenured users.
 *
 * Empty state: a gentle nudge to check in. Pull-to-refresh
 * piggy-backs the browser's native pull when supported (touchstart
 * at scrollTop=0 → /feed/generate).
 * ────────────────────────────────────────────────────────────────── */

const PAGE_STYLES = `
  .intel-root { font-family: 'DM Sans', sans-serif; background: ${PALETTE.bg}; min-height: 100vh; color: ${PALETTE.text}; max-width: 460px; margin: 0 auto; padding-bottom: 120px; }
  .intel-serif { font-family: 'Playfair Display', Georgia, serif; }

  @keyframes intelFadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .intel-fade { animation: intelFadeUp 0.45s ease forwards; }

  /* DeepDiveCard hardcodes "fu d4"/"fu d4 section-card" — same
     definitions as Dashboard/Profile, reproduced here so the
     embedded card's fade-in and (locked/teaser) card styling work
     without touching DeepDiveCard.jsx itself. */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fu  { animation: fadeUp 0.5s ease forwards; opacity: 0; }
  .d1  { animation-delay: 0.05s; }
  .d2  { animation-delay: 0.15s; }
  .d3  { animation-delay: 0.25s; }
  .d4  { animation-delay: 0.35s; }
  .section-card {
    background: ${PALETTE.surface};
    border-radius: 20px;
    padding: 22px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    margin-bottom: 12px;
  }

  .intel-section-header {
    font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
    font-weight: 500; color: ${PALETTE.primary};
    padding: 28px 24px 10px;
  }
  .intel-section-count {
    color: ${PALETTE.textFaint}; margin-left: 6px;
  }

  .intel-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 11px;
    border-radius: 99px;
    background: ${PALETTE.surface};
    border: 1px solid ${PALETTE.border};
    cursor: pointer;
    font-size: 11px; color: ${PALETTE.text}; font-weight: 400;
    transition: background 0.15s, border-color 0.15s;
    min-height: 28px;
  }
  .intel-chip:hover { background: ${PALETTE.surfaceAlt}; }
  .intel-chip-domain {
    font-size: 9px; color: ${PALETTE.primary};
    letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600;
  }

  .intel-back {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: none; padding: 8px 0;
    color: ${PALETTE.text}; font-size: 13px;
    cursor: pointer;
    min-height: 44px;
  }
  .intel-show-more {
    background: none; border: none; padding: 12px 24px;
    color: ${PALETTE.primary}; font-weight: 500;
    font-size: 12.5px; letter-spacing: 0.02em;
    cursor: pointer;
    min-height: 44px;
  }
`;


/**
 * Bucket assignment based on the item's `created_at` relative to
 * `now`. Computes once per render in `useMemo`.
 *
 * Bucket boundaries are calculated against the user's *local*
 * midnight (not UTC) so a feed item created at 10pm Pacific lands
 * in the same bucket the user actually experienced it in.
 */
function bucketize(items, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfWeek = startOfToday - 6 * 24 * 60 * 60 * 1000;

  const buckets = { today: [], yesterday: [], thisWeek: [], earlier: [] };
  items.forEach((item) => {
    const ts = item.created_at ? new Date(item.created_at).getTime() : 0;
    if (ts >= startOfToday) buckets.today.push(item);
    else if (ts >= startOfYesterday) buckets.yesterday.push(item);
    else if (ts >= startOfWeek) buckets.thisWeek.push(item);
    else buckets.earlier.push(item);
  });
  return buckets;
}


// Per-bucket initial reveal cap. "Show N more →" extends the cap
// by the same step. Picked at 10 to give first-page users a full
// scrollable section without flooding the screen for power users
// with hundreds of historical signals.
const BUCKET_PAGE_SIZE = 10;


function formatDayShort(date) {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}


/**
 * Compact date label appended to each section header.
 *
 * Conventions:
 *   • TODAY      → "May 13"
 *   • YESTERDAY  → "May 12"
 *   • THIS WEEK  → "May 7–12" when the bucket spans multiple days,
 *                  "May 12" when only one day produced items.
 *   • EARLIER    → "before May 7" so the user knows where the cutoff
 *                  sits without needing a tooltip.
 */
function dateRangeLabel(bucketKey, items, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (bucketKey === "today") return formatDayShort(today);
  if (bucketKey === "yesterday") {
    const y = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    return formatDayShort(y);
  }
  if (bucketKey === "earlier") {
    const cutoff = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    return `before ${formatDayShort(cutoff)}`;
  }
  // thisWeek — compute from actual item dates so the header reflects
  // what's in this bucket, not the whole week. Falls back to the
  // week-window dates if items don't carry usable timestamps.
  if (!items || items.length === 0) return "";
  const tsValues = items
    .map((i) => (i.created_at ? new Date(i.created_at) : null))
    .filter((d) => d && !isNaN(d.getTime()));
  if (tsValues.length === 0) return "";
  const min = new Date(Math.min(...tsValues.map((d) => d.getTime())));
  const max = new Date(Math.max(...tsValues.map((d) => d.getTime())));
  const minLabel = formatDayShort(min);
  const maxLabel = formatDayShort(max);
  if (minLabel === maxLabel) return minLabel;
  // Compact dash range when same month, full range otherwise.
  if (min.getMonth() === max.getMonth()) {
    return `${min.toLocaleDateString(undefined, { month: "short" })} ${min.getDate()}–${max.getDate()}`;
  }
  return `${minLabel} – ${maxLabel}`;
}


/**
 * Pick the most-recent signal per domain for the summary chips.
 * Falls back to the item's `type` when domain is missing so the
 * chip still says something useful.
 */
function mostRecentByDomain(items) {
  const out = { BIO: null, PSYCH: null, BEH: null };
  for (const item of items) {
    const d = (item.domain || "").toUpperCase();
    if (d && out[d] === null && (d === "BIO" || d === "PSYCH" || d === "BEH")) {
      out[d] = item;
    }
    if (out.BIO && out.PSYCH && out.BEH) break;
  }
  return out;
}


function scrollToCard(itemId) {
  const el = document.getElementById(`feed-card-${itemId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Brief highlight pulse so the user's eye locks onto the card.
  el.style.boxShadow = "0 0 0 2px rgba(158,132,86,0.4)";
  setTimeout(() => { el.style.boxShadow = ""; }, 1100);
}


export default function Intelligence() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [refreshing, setRefreshing] = useState(false);
  // Per-bucket reveal caps. Each "Show N more →" tap bumps the
  // matching key by BUCKET_PAGE_SIZE. Reset on a successful
  // refresh — the user re-opening with fresh data should start at
  // the first page, not whatever offset they had paged into.
  const [reveal, setReveal] = useState({
    today: BUCKET_PAGE_SIZE,
    yesterday: BUCKET_PAGE_SIZE,
    thisWeek: BUCKET_PAGE_SIZE,
    earlier: BUCKET_PAGE_SIZE,
  });

  // Initial load. Asks for a large window (100) so we can bucket
  // client-side without paginating per bucket via round-trips. The
  // backend supports ``offset`` for future deep-pagination passes
  // but day-to-day usage tops out well under 100 items.
  useEffect(() => {
    if (!token) return;
    feedApi.get(token, { limit: 100 })
      .then((data) => setItems(data?.items || []))
      .catch(() => setItems([]));
  }, [token]);

  const markRead = (itemId) => {
    feedApi.markRead(itemId, token).catch(() => {});
    setItems((prev) => prev?.map((i) => (i.id === itemId ? { ...i, read: true } : i)) || prev);
  };

  const refresh = async () => {
    if (!token || refreshing) return;
    setRefreshing(true);
    try {
      const data = await feedApi.generate(token);
      setItems(data?.items || []);
      // Reset reveal counts so the new page doesn't open with an
      // arbitrary "showing 30 of 4" state.
      setReveal({
        today: BUCKET_PAGE_SIZE,
        yesterday: BUCKET_PAGE_SIZE,
        thisWeek: BUCKET_PAGE_SIZE,
        earlier: BUCKET_PAGE_SIZE,
      });
    } catch {
      // Soft failure — keep showing the cached list.
    } finally {
      setRefreshing(false);
    }
  };

  const showMore = (bucketKey) =>
    setReveal((prev) => ({ ...prev, [bucketKey]: (prev[bucketKey] || 0) + BUCKET_PAGE_SIZE }));

  const sorted = useMemo(() => {
    const arr = (items || []).slice();
    arr.sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return arr;
  }, [items]);

  const buckets = useMemo(() => bucketize(sorted), [sorted]);
  const recentByDomain = useMemo(() => mostRecentByDomain(sorted), [sorted]);

  const totalCount = sorted.length;
  const isLoading = items === null;
  const isEmpty = !isLoading && totalCount === 0;

  const renderCards = (list) =>
    list.map((item) => (
      <FeedCard
        key={item.id}
        item={item}
        onMarkRead={markRead}
      />
    ));

  return (
    <>
      <style>{PAGE_STYLES}</style>
      <div className="intel-root">
        {/* Header */}
        <div style={{ padding: "max(20px, calc(env(safe-area-inset-top) + 12px)) 24px 6px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button className="intel-back" onClick={() => navigate(-1)} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span style={{ fontSize: 13, color: PALETTE.text }}>Back</span>
          </button>
          <button
            onClick={refresh}
            disabled={refreshing}
            aria-label="Refresh"
            style={{
              background: "none", border: "none", padding: 8,
              cursor: refreshing ? "default" : "pointer",
              color: PALETTE.primary,
              minHeight: 44, minWidth: 44,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              opacity: refreshing ? 0.4 : 1,
            }}
          >
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round"
              style={refreshing ? { animation: "intelFadeUp 0.45s ease forwards" } : undefined}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
          </button>
        </div>

        {/* Title */}
        <div className="intel-fade" style={{ padding: "8px 24px 0" }}>
          <h1
            className="intel-serif"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontStyle: "italic",
              fontSize: 28, fontWeight: 400,
              color: "#1C1814",
              lineHeight: 1.2,
              marginBottom: 6,
            }}
          >
            Intelligence
          </h1>
          <p style={{ fontSize: 12.5, color: PALETTE.textMid, fontWeight: 300 }}>
            What Haiven has been noticing.
          </p>
        </div>

        {/* Summary card */}
        {!isLoading && !isEmpty && (
          <div className="intel-fade" style={{ padding: "20px 24px 0" }}>
            <div style={{
              background: "#FDFAF6",
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 16,
              padding: "18px 18px 16px",
              boxShadow: "0 1px 10px rgba(28,24,20,0.05)",
            }}>
              <p style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 17, lineHeight: 1.4,
                color: "#1C1814", fontWeight: 400,
                marginBottom: 14,
              }}>
                This week Haiven noticed{" "}
                <span style={{ color: PALETTE.primary, fontStyle: "italic" }}>
                  {totalCount} {totalCount === 1 ? "thing" : "things"}
                </span>{" "}
                about you.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {DOMAIN_META.map(({ code: dom }) => {
                  const item = recentByDomain[dom];
                  if (!item) return null;
                  return (
                    <button
                      key={dom}
                      className="intel-chip"
                      onClick={() => scrollToCard(item.id)}
                      aria-label={`Jump to ${DOMAIN_WORD[dom] || dom} signal`}
                    >
                      <span className="intel-chip-domain">{DOMAIN_WORD[dom] || dom}</span>
                      <span style={{
                        maxWidth: 180,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {item.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Deep Dive entry point (moved from Profile). Sits between
            the week summary and the itemized feed — a standing
            90-day cross-cutting analysis, not a dated feed item, so
            it doesn't belong inside the Today/Yesterday/This week
            buckets below. Fully self-contained (fetches its own
            state, self-gates on check-in count); its dark-shell
            states already read as visually distinct from the light
            FeedCards beneath it. */}
        {!isLoading && !isEmpty && (
          <div style={{ padding: "16px 24px 0" }}>
            <DeepDiveCard />
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="intel-fade" style={{ padding: "40px 24px" }}>
            <div style={{
              background: "#FDFAF6",
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 16,
              padding: "22px",
              textAlign: "center",
            }}>
              <p style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontStyle: "italic",
                fontSize: 16, color: "#1C1814",
                marginBottom: 10, lineHeight: 1.4,
              }}>
                Nothing new since your last check-in.
              </p>
              <p style={{ fontSize: 13, color: PALETTE.textMid, fontWeight: 300, marginBottom: 16 }}>
                Check in to generate fresh signals.
              </p>
              <button
                onClick={() => navigate("/checkin")}
                style={{
                  background: PALETTE.primary, color: "#F2EDE3",
                  border: "none", borderRadius: 10,
                  padding: "12px 22px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 500,
                  cursor: "pointer", minHeight: 44,
                }}
              >
                Check in now
              </button>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div style={{ padding: "20px 24px 0" }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{
                background: "#FDFAF6", border: `1px solid ${PALETTE.border}`,
                borderRadius: 14, padding: 16, marginBottom: 14,
                height: 110,
              }} />
            ))}
          </div>
        )}

        {/* Feed buckets — per-bucket pagination, 10 at a time */}
        {!isLoading && !isEmpty && (
          <div style={{ paddingBottom: 24 }}>
            {[
              { key: "today",     label: "Today" },
              { key: "yesterday", label: "Yesterday" },
              { key: "thisWeek",  label: "This week" },
              { key: "earlier",   label: "Earlier" },
            ].map(({ key, label }) => {
              const all = buckets[key];
              if (!all || all.length === 0) return null;
              const cap = reveal[key];
              const shown = all.slice(0, cap);
              const hiddenCount = all.length - shown.length;
              const remaining = Math.min(hiddenCount, BUCKET_PAGE_SIZE);
              const range = dateRangeLabel(key, all);
              const sigWord = all.length === 1 ? "signal" : "signals";
              return (
                <div key={key}>
                  <div className="intel-section-header">
                    {label}
                    <span className="intel-section-count">
                      · {all.length} {sigWord}
                      {range ? ` · ${range}` : ""}
                    </span>
                  </div>
                  <div style={{ padding: "0 24px" }}>{renderCards(shown)}</div>
                  {hiddenCount > 0 && (
                    <button
                      className="intel-show-more"
                      onClick={() => showMore(key)}
                    >
                      Show {remaining} more →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </>
  );
}
